import { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { DOCKER_LAUNCHED,BASE_WEBSOCKET,BASEURL, wsProtocol, MAP_CENTER, IS_INDOOR, LINEAR_SPEED, ANGULAR_SPEED, VERIFICATION_PASS, WEB_RTC_SERVER, ROS_HOSTNAME, PTZ_SERVER, USE_PERCENTAGE, ESP_SERVICES, HAS_THERMAL, IS_SIMPLE, HAS_GAS } from '../constants/constants';

interface RobotDetails {
  robotName: string;
  rosHostname: string;
  rosPort: string;
  baseWebSocket: string;
  rosWebsocket: string;
  webRTCServer: string;
  ptzServer: string;
  isIndoor: boolean;
  mapCenter: number[];
  baseUrl: string;
  altBaseUrl: string;
  mediaUrl: string;
  realTimeUpdatesUrl: string;
  linearSpeed: number;
  angularSpeed: number;
  verificationPass: string;
  usePercent: boolean;
  useServices: boolean;
  hasThermal: boolean;
  simpleRec: boolean;
  hasGas: boolean;
}

interface RobotDetailsContextType {
  robotDetails: RobotDetails;
  setRobot: (robotName: string) => void;
  setRobotDetails: (newRobotDetails: Partial<RobotDetails>) => void;
}

const RobotDetailsContext = createContext<RobotDetailsContextType | undefined>(undefined);

export const useRobotDetails = () => {
  const context = useContext(RobotDetailsContext);
  if (!context) throw new Error("useRobotDetails must be used within a RobotDetailsProvider");
  return context;
};

export const RobotDetailsProvider = ({ children }: { children: ReactNode }) => {
  const rawRobotConfigs = process.env.REACT_APP_ROBOT_CONFIGS ?? '{}';
  const defaultRobotName = process.env.REACT_APP_ROBOTNAME ?? "local";
  let hyper = "https"
  if (DOCKER_LAUNCHED === true){
    hyper = "http"
  }

  const [robotName, setRobotName] = useState<string>(defaultRobotName);
  const [robotDetails, setRobotDetailsState] = useState<RobotDetails>(() => createRobot(defaultRobotName));

  function createRobot(name: string): RobotDetails {
    let robotConfig: Partial<RobotDetails> = {};
    try {
      const parsed = JSON.parse(rawRobotConfigs);
      if (name in parsed) {
        robotConfig = parsed[name];
      } else {
        console.warn(`Robot config for '${name}' not found in REACT_APP_ROBOT_CONFIGS`);
      }
    } catch (err) {
      console.error("Failed to parse REACT_APP_ROBOT_CONFIGS:", err);
    }

    const {
      rosHostname = ROS_HOSTNAME,
      rosPort = "9090",
      baseWebSocket = BASE_WEBSOCKET,
      rosWebsocket,
      webRTCServer = WEB_RTC_SERVER,
      ptzServer = PTZ_SERVER,
      isIndoor = IS_INDOOR,
      mapCenter = MAP_CENTER,
      baseUrl = BASEURL,
      altBaseUrl = BASEURL,
      mediaUrl,
      realTimeUpdatesUrl,
      linearSpeed = LINEAR_SPEED,
      angularSpeed = ANGULAR_SPEED,
      verificationPass = VERIFICATION_PASS,
      usePercent = USE_PERCENTAGE,
      useServices = ESP_SERVICES,
      hasThermal = HAS_THERMAL,
      simpleRec= IS_SIMPLE,
      hasGas = HAS_GAS
    } = robotConfig;

    const fullBaseUrl =
      process.env.NODE_ENV === 'production'
        ? `${hyper}://${baseUrl}`
        : `${window.location.protocol}//${baseUrl}`;

    const fullAltBaseUrl =
      process.env.NODE_ENV === 'production'
        ? `${hyper}://${altBaseUrl}`
        : `${window.location.protocol}//${altBaseUrl}`;

    const fullMediaUrl = mediaUrl ?? fullBaseUrl;
    const fullRealTimeUpdatesUrl = realTimeUpdatesUrl ?? `${wsProtocol}${baseUrl}/api/ws/notify`;
    const fullRosWebsocket = rosWebsocket ?? `${wsProtocol}${baseWebSocket}:${rosPort}`;

    return {
      robotName: name,
      rosHostname,
      rosPort,
      baseWebSocket,
      rosWebsocket: fullRosWebsocket,
      webRTCServer,
      ptzServer,
      isIndoor,
      mapCenter,
      baseUrl: fullBaseUrl,
      altBaseUrl: fullAltBaseUrl,
      mediaUrl: fullMediaUrl,
      realTimeUpdatesUrl: fullRealTimeUpdatesUrl,
      linearSpeed,
      angularSpeed,
      verificationPass,
      usePercent,
      useServices,
      hasThermal,
      simpleRec,
      hasGas
    };
  }

  const setRobot = (newRobotName: string) => {
    setRobotName(newRobotName);
  };

  const setRobotDetails = (newDetails: Partial<RobotDetails>) => {
    setRobotDetailsState(prev => ({ ...prev, ...newDetails }));
  };

  useEffect(() => {
    const newRobotDetails = createRobot(robotName);
    setRobotDetailsState(newRobotDetails);
  }, [robotName]);

  return (
    <RobotDetailsContext.Provider value={{ robotDetails, setRobot, setRobotDetails }}>
      {children}
    </RobotDetailsContext.Provider>
  );
};
